import { useState } from 'react'
import {
    LuArrowRight,
    LuBookOpen,
    LuCalendarDays,
    LuFlower2,
    LuHeart,
    LuLightbulb,
    LuMapPin,
    LuMessageCircle,
    LuMusic2,
    LuOrbit,
    LuPalette,
    LuPencil,
    LuPenTool,
    LuSparkles,
    LuSprout,
} from 'react-icons/lu'
import projects from '../../data/projects'
import ProjectModal from './ProjectModal'
import ProjectArtwork from './ProjectArtwork'
import MakerDoodle from '../MakerDoodle'
import FloatingIcons from '../FloatingIcons'
import './SectionAccents.css'
import './Projects.css'

const projectAccents = {
    portal: { Icon: LuFlower2, note: 'For curious classrooms.' },
    express: { Icon: LuBookOpen },
    portfolio: { Icon: LuHeart, note: 'My little corner, made by hand.' },
    hues: { Icon: LuPalette },
    lilypad: { Icon: LuFlower2 },
    society: { Icon: LuSprout },
    terra: { Icon: LuOrbit },
    website: { Icon: LuPenTool },
    jobs: { Icon: LuMapPin },
    logic: { Icon: LuLightbulb },
    language: { Icon: LuMessageCircle },
    notes: { Icon: LuPencil },
    music: { Icon: LuMusic2 },
    focus: { Icon: LuBookOpen },
}

const updateCardTilt = (event) => {
    if (
        event.pointerType !== 'mouse' ||
        !window.matchMedia('(hover: hover) and (pointer: fine)').matches
    )
        return

    const card = event.currentTarget
    const bounds = card.getBoundingClientRect()
    const x = Math.max(
        -0.5,
        Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5)
    )
    const y = Math.max(
        -0.5,
        Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5)
    )

    card.style.setProperty('--card-tilt-x', `${(-y * 7).toFixed(2)}deg`)
    card.style.setProperty('--card-tilt-y', `${(x * 7).toFixed(2)}deg`)
    card.style.setProperty('--art-shift-x', `${(-x * 4).toFixed(2)}px`)
    card.style.setProperty('--art-shift-y', `${(-y * 4).toFixed(2)}px`)
}

const resetCardTilt = (event) => {
    const card = event.currentTarget
    for (const property of [
        '--card-tilt-x',
        '--card-tilt-y',
        '--art-shift-x',
        '--art-shift-y',
    ]) {
        card.style.removeProperty(property)
    }
}

const ProjectCard = ({ project, number, onSelect }) => {
    const { Icon: KeepsakeIcon, note } = projectAccents[project.artwork] || {
        Icon: LuSparkles,
    }
    const technologies = project.tech ? project.tech.split(' · ') : []
    const visibleTechnologies = project.featured
        ? technologies
        : technologies.slice(0, 4)
    const remainingCount = technologies.length - visibleTechnologies.length

    return (
        <article
            className={
                'project-card' +
                (project.featured ? ' project-card--featured' : '')
            }
            data-reveal
            data-artwork={project.artwork}
            onPointerMove={updateCardTilt}
            onPointerLeave={resetCardTilt}
            onPointerCancel={resetCardTilt}
            onBlur={resetCardTilt}
        >
            <div className="project-card__cover">
                <ProjectArtwork
                    project={project}
                    className="project-card__artwork"
                />
                <div className="project-card__keepsake" aria-hidden="true">
                    <KeepsakeIcon />
                    <span>{String(number).padStart(2, '0')}</span>
                </div>
            </div>

            <div className="project-card__body">
                <p className="project-card__category">{project.category}</p>
                <h4 className="project-card__title">{project.title}</h4>
                <p className="project-card__date">
                    <LuCalendarDays aria-hidden="true" /> {project.date}
                </p>
                <p className="project-card__description">{project.desc}</p>
                {project.featured && note && (
                    <p className="project-card__personal-note">{note}</p>
                )}

                {technologies.length > 0 && (
                    <ul
                        className="project-card__technologies"
                        aria-label="Technologies"
                    >
                        {visibleTechnologies.map((technology) => (
                            <li className="project-tech" key={technology}>
                                {technology}
                            </li>
                        ))}
                        {remainingCount > 0 && (
                            <li
                                className="project-tech project-tech--more"
                                title={technologies.slice(4).join(', ')}
                                aria-label={
                                    'Also built with ' +
                                    technologies.slice(4).join(', ')
                                }
                            >
                                +{remainingCount} more
                            </li>
                        )}
                    </ul>
                )}

                <div className="project-card__footer">
                    <button
                        type="button"
                        className="project-card__action"
                        aria-label={
                            'View ' + project.title + ' project details'
                        }
                        onClick={() => onSelect(project)}
                    >
                        View project
                        <span className="project-card__arrow">
                            <LuArrowRight aria-hidden="true" />
                        </span>
                    </button>
                </div>
            </div>
        </article>
    )
}

const Projects = () => {
    const [selectedProject, setSelectedProject] = useState(null)
    const featured = projects.filter((project) => project.featured)
    const nonFeatured = projects.filter((project) => !project.featured)

    return (
        <section
            id="projects"
            className="projects-section"
            aria-labelledby="projects-title"
        >
            <FloatingIcons variant="projects" />
            <div className="projects-section__container">
                <header className="projects-section__intro" data-reveal>
                    <div>
                        <div className="projects-section__label">
                            <p className="projects-section__eyebrow">
                                01 / Things I've made
                            </p>
                            <MakerDoodle
                                variant="spark"
                                className="projects-section__doodle"
                            />
                        </div>
                        <div className="projects-section__title-row">
                            <h2 id="projects-title">
                                Selected projects<span>.</span>
                            </h2>
                            <span
                                className="projects-section__corner-doodle"
                                aria-hidden="true"
                            >
                                <MakerDoodle variant="flower" />
                            </span>
                        </div>
                        <p className="handwritten projects-section__annotation">
                            A collection of things close to my heart.
                        </p>
                    </div>
                    <p className="projects-section__summary">
                        Learning experiences, useful little tools, and creative
                        experiments. Each one has its own story, and I'm happy
                        to share a few.
                    </p>
                </header>

                {featured.length > 0 && (
                    <div className="projects-section__group">
                        <div
                            className="projects-section__group-heading"
                            data-reveal
                        >
                            <h3>
                                <LuSparkles aria-hidden="true" /> Featured work
                            </h3>
                        </div>
                        <div className="projects-grid projects-grid--featured">
                            {featured.map((project, index) => (
                                <ProjectCard
                                    key={project.title}
                                    project={project}
                                    number={index + 1}
                                    onSelect={setSelectedProject}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div className="projects-section__group">
                    <div
                        className="projects-section__group-heading"
                        data-reveal
                    >
                        <h3>More to explore</h3>
                    </div>
                    <div className="projects-grid">
                        {nonFeatured.map((project, index) => (
                            <ProjectCard
                                key={project.title}
                                project={project}
                                number={featured.length + index + 1}
                                onSelect={setSelectedProject}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <ProjectModal
                project={selectedProject}
                onClose={() => setSelectedProject(null)}
            />
        </section>
    )
}

export default Projects
